// Polyfills
import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { fetchEventSource } from '@microsoft/fetch-event-source';

require('fast-text-encoding');

import * as DOMPurify from 'dompurify';
import { marked } from 'marked';
import $ from 'jquery';
import debounce from 'lodash.debounce';
import { __ } from '@wordpress/i18n';
import getFormData from './helpers/get-form-data';
import GCOAIField from './field';

class ClientSideError extends Error {}
class ServerError extends Error {}

interface GCOAIFieldStreamArgs {
	formId: number;
	fieldId: number;
	triggerMethod: string;
	dependencies: number[];
	enableAutoScrolling: boolean;
	nonce: string;
}

class GCOAIFieldStream extends GCOAIField {
	public rawContent: string = '';

	public dependencies: number[];
	public enableAutoScrolling: boolean;

	public $inputThreadId: JQuery<HTMLInputElement>;
	public $inputChatId: JQuery<HTMLInputElement>;

	public abortController?: AbortController;

	/**
	 * Used to namespace event listeners, data, etc.
	 */
	public instanceId: string;

	public constructor(args: GCOAIFieldStreamArgs) {
		super(args);

		this.dependencies = args.dependencies;
		this.enableAutoScrolling = args.enableAutoScrolling;

		this.$inputThreadId =
			this.$field.find<HTMLInputElement>('.gcoai-thread-id');

		this.$inputChatId =
			this.$field.find<HTMLInputElement>('.gcoai-chat-id');

		this.waitingForInputText = __('Waiting for input…', 'gc-openai');

		this.instanceId = Math.random().toString(36).substring(7);

		this.init();

		// If the trigger method is merge tag value change, show the waiting for input message instead of loading.
		if (this.triggerMethod === 'merge_tag_value_change') {
			this.addPlaceholder(this.waitingForInputText);
		}
	}

	public rehydrate() {
		this.rawContent = this.$inputResponse.val()!;
		this.setOutput();
	}

	public bind() {
		super.bind();

		// TODO investigate why stream is firing if form load is the event and conditional logic gets triggered. This is fine, I think, but I'd like to understand why.
		// TODO addendum, if the OpenAI field is conditionally hidden and the trigger method is load, do not stream until the field has been made visible.

		if (
			this.dependencies.length &&
			this.triggerMethod === 'merge_tag_value_change'
		) {
			this.$form.off('.gcoai');

			// This needs some tuning up similar to GPPA. It sends requests more often than it should.
			this.$form.on(
				`keyup.gcoai${this.instanceId} change.gcoai${this.instanceId} DOMAutoComplete.gcoai${this.instanceId} paste.gcoai${this.instanceId}`,
				'[name^="input_"]',
				debounce((event) => {
					// Figure out if the input being modified is one of the dependencies.
					const isDependency = this.dependencies.some(
						(dependency) => {
							return (
								// eslint-disable-next-line eqeqeq
								String(dependency) ==
								$(event.target)
									.attr('name')!
									.replace('input_', '')
							);
						}
					);

					if (!isDependency) {
						return;
					}

					// If the value hasn't changed, don't trigger the event.
					if (
						$(event.target).data(
							`gcoaiLastValue_${this.instanceId}`
						) === $(event.target).val()
					) {
						// eslint-disable-next-line no-console
						console.debug(
							`Input value hasn't changed, not triggering event.`
						);
						return;
					}

					// Set data on the input that triggered the event containing the last value so we don't trigger the event again.
					$(event.target).data(
						`gcoaiLastValue_${this.instanceId}`,
						$(event.target).val()
					);

					this.generate();
				}, 250)
			);
		}
	}

	public handleEventSourceMessage(event: EventSourceMessage) {
		let data;
		try {
			data = JSON.parse(event.data);
		} catch (err) {
			// noop, the data was likely an empty string
			// eslint-disable-next-line no-console
			console.error(err);
			return;
		}


		if (data.error) {
			this.setOutput(data.error);
			this.abortController?.abort();
		}

		if (this.$output.find('.gcoai-placeholder').length) {
			this.$output.html('');
		}

		if (this.$inputThreadId.length && data?.thread_id) {
			this.$inputThreadId.val(data.thread_id);
		}

		if (this.$inputChatId.length && data?.chat_id) {
			// We use transients to store chats as they are ephemeral. This is our way of validating chats on submission.
			this.$inputChatId.val(data.chat_id);
		}

		const { delta } = data;

		if (delta) {
			this.rawContent += delta;
			this.setOutput();

			this.$inputResponse.val(this.rawContent);

			// Trigger an event on $inputResponse so that GF conditional logic can pick up the change.
			this.$inputResponse.trigger('change');

			if (this.enableAutoScrolling) {
				// Calculate the bottom position of the output element
				const outputBottom =
					this.$output.offset()!.top +
					this.$output.outerHeight()! -
					$(window).height()!;

				// Scroll the document to the bottom of the output
				$('html, body').scrollTop(outputBottom);
			}
		}
	}

	public setOutput(content?: string) {
		if (!content) {
			content = this.rawContent;
		}

		this.$output.html(DOMPurify.sanitize(marked(content) as string));
	}

	public generate() {
		// Abort previous request if it's in flight.
		this.abortController?.abort();

		// Hide the trigger button and show the output.
		this.$buttonTrigger.hide();
		this.$output.show();

		// Disable the submit button while we're loading.
		this.disableSubmitButton();

		// Hide the regenerate button while we're loading. Eventually we could add a cancel button.
		this.$buttonRegenerate.hide();

		// Replace the output with a loading message.
		this.addPlaceholder(this.loadingText);

		/*
		 * Clear the raw content that we've accumulated so far. This is what gets
		 * displayed in the output after being processed by the markdown parser and sanitizer.
		 */
		this.rawContent = '';

		// Create a new AbortController for this request.
		this.abortController = new AbortController();

		const entryId = new URLSearchParams(window.location.search).get('lid');

		// Action has to be passed as a query parameter because we're sending JSON.
		fetchEventSource(
			window.gc_openai_field_stream_strings.ajaxUrl +
				`?action=gc_openai_get_prompt&entryId=${entryId}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					formData: getFormData(this.$form[0]),
					formId: this.formId,
					fieldId: this.fieldId,
					nonce: this.nonce,
					entryNonce: this.entryNonce,
				}),
				openWhenHidden: true, // Prevent aborting and opening new connection when tab is unfocused.
				onopen: async (response) => {
					if (
						response.ok &&
						response.headers
							?.get('content-type')
							?.includes('text/event-stream')
					) {
						// everything's good
					} else if (
						response.status >= 400 &&
						response.status < 500 &&
						response.status !== 429
					) {
						// client-side errors are usually non-retriable:
						throw new ClientSideError();
					} else {
						throw new ServerError();
					}
				},
				onmessage: (msg: EventSourceMessage) => {
					this.handleEventSourceMessage(msg);
				},
				onclose: () => {
					this.abortController?.abort();

					this.$buttonRegenerate.show();

					// Enable the submit button again.
					this.enableSubmitButton();
				},
				onerror: (err) => {
					// eslint-disable-next-line no-console
					console.error(
						'There was an error streaming the response.',
						err
					);

					// Update the output with an error message.
					this.$output.html(
						`<div class="gcoai-error">${__(
							'There was an error streaming the response.',
							'gc-openai'
						)}</div>`
					);

					this.abortController?.abort();
					this.$buttonRegenerate.show();

					// Enable the submit button again.
					this.enableSubmitButton();

					// Re-throw the error so it doesn't try to reconnect.
					throw err;
				},
				signal: this.abortController.signal,
			}
		);
	}
}

(window as any).GCOAIFieldStream = GCOAIFieldStream;
